import React, { useState, useEffect } from 'react';
import type { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Form, { Field, FormFooter, ErrorMessage, MessageWrapper } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import { CloseButton, ModalBody, useModal } from '@atlaskit/modal-dialog';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import TextArea from '@atlaskit/textarea';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import { SnippetEditor } from '../../editor/SnippetEditor.tsx';
import type { SnippetScope } from '@confluence/fabric-media-support/entry-points/snippetScope.ts';

import { messages } from './SnippetBaseModal.i18n.ts';
import { MODAL_OUTCOME, ANALYTICS_OPERATIONAL_SUBJECT } from '../../constants/snippetAnalytics.ts';
import {
	MAX_SNIPPET_NAME_LENGTH,
	SNIPPET_NAME_CUTOFF_LENGTH,
	MAX_SNIPPET_DESCRIPTION_LENGTH,
	SNIPPET_DESCRIPTION_CUTOFF_LENGTH,
	type SnippetBaseModalFormValues,
} from '../utils/snippetModalFormUtils.ts';
import { AIInputField } from '../components/AIInputField.tsx';
import { SnippetCharacterCounterField } from '../components/SnippetCharacterCounterField.tsx';
import { SnippetSharingField } from '../components/SnippetSharingField.tsx';
import { isAdfBodyEmpty } from '../../utils/adfUtils.ts';

export type SnippetBaseModalProps = {
	title: React.ReactNode;
	submitLabel: React.ReactNode;
	formKey: string;
	defaultValues: SnippetBaseModalFormValues;
	initialAdf: DocNode | { type: 'doc'; version: 1; content: any[] };
	onSubmit: (values: SnippetBaseModalFormValues, currentAdf: DocNode) => Promise<void>;
	onClose: () => void;
	/** Called when user clicks Cancel (optional; defaults to onClose) */
	onCancel?: () => void;
	onCloseComplete: () => void;
	contentId: string;
	spaceId: string;
	spaceKey: string;
	snippetId?: string;
	/** Snippet scope — when GLOBAL, skips snippet-specific media token fetch */
	snippetScope?: SnippetScope;
	isSnippetSharingForSiteEnabled: boolean;
	onADFError?: (error: Error | null) => void;
	/** When true, wraps name/description fields in AIInputField and disables them during loading. */
	isAiSuggestionLoading?: boolean;
	extraActions?: React.ReactNode;
	/** Optional warning rendered above the form fields. */
	formWarning?: React.ReactNode;
};

export type GetSnippetBaseModalPropsArgs = {
	title: React.ReactNode;
	submitLabel: React.ReactNode;
	formKey: string;
	defaultValues: SnippetBaseModalFormValues;
	initialAdf: DocNode | { type: 'doc'; version: 1; content: any[] };
	onSubmit: (values: SnippetBaseModalFormValues, currentAdf: DocNode) => Promise<void>;
	handleClose: (attrs: Record<string, unknown>) => void;
	fireFailedAnalytic: (subject: string, attrs?: Record<string, unknown>) => void;
	onCloseComplete: () => void;
	contentId: string;
	spaceId: string;
	spaceKey: string;
	snippetId?: string;
	snippetScope?: SnippetScope;
	isSnippetSharingForSiteEnabled: boolean;
	isAiSuggestionLoading?: boolean;
	extraActions?: React.ReactNode;
	/** Optional warning rendered above the form fields. */
	formWarning?: React.ReactNode;
};

export function getSnippetBaseModalProps(
	args: GetSnippetBaseModalPropsArgs,
): SnippetBaseModalProps {
	const { handleClose, fireFailedAnalytic, ...rest } = args;
	return {
		...rest,
		onClose: () => handleClose({ outcome: MODAL_OUTCOME.CLOSED }),
		onCancel: () => handleClose({ outcome: MODAL_OUTCOME.CANCELLED }),
		onADFError: (error: Error | null) => {
			if (error) {
				fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_ADF_SERIALIZATION, {
					error: error?.message ?? 'Unknown error',
				});
			}
		},
	};
}

export const EMPTY_ADF_ERROR = 'empty_adf' as const;

const styles = cssMap({
	modalContent: {
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.xlarge'),
	},
	bodyWrapper: {
		display: 'flex',
		flex: 1,
		height: '80vh',
		overflow: 'hidden',
	},
	bodyContainer: {
		display: 'flex',
		flexDirection: 'row',
		gap: token('space.200'),
		flex: 1,
		overflow: 'hidden',
		paddingInline: token('space.050'),
		paddingTop: token('space.150'),
		paddingBottom: token('space.200'),
		boxSizing: 'border-box',
	},
	headerContainer: {
		paddingTop: token('space.150'),
		paddingLeft: token('space.400'),
		paddingRight: token('space.300'),
		flexShrink: 0,
	},
	previewColumn: {
		flex: 1,
		height: '100%',
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface'),
		boxShadow: token('elevation.shadow.raised'),
		transition: 'box-shadow 300ms ease-out',
		overflow: 'auto',
	},
	formColumn: {
		width: '320px',
		minWidth: '320px',
		flexShrink: 0,
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface'),
		boxShadow: token('elevation.shadow.raised'),
		transition: 'box-shadow 300ms ease-out',
		padding: token('space.300'),
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	},
	formContainer: {
		margin: 0,
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
	},
	formFields: {
		flex: 1,
	},
	formActions: {
		flexShrink: 0,
		paddingTop: token('space.200'),
	},
});

const SnippetBaseModalHeader: FC<{
	title: React.ReactNode;
	onClose: () => void;
}> = ({ title, onClose }) => {
	const { titleId } = useModal();
	return (
		<Inline
			alignInline="center"
			spread="space-between"
			alignBlock="center"
			xcss={styles.headerContainer}
		>
			<Heading size="small" id={titleId}>
				{title}
			</Heading>
			<CloseButton onClick={onClose} />
		</Inline>
	);
};

export const SnippetBaseModal: FC<SnippetBaseModalProps> = ({
	title,
	submitLabel,
	formKey,
	defaultValues,
	initialAdf,
	onSubmit,
	onClose,
	onCancel,
	onCloseComplete,
	contentId,
	spaceId,
	spaceKey,
	snippetId,
	snippetScope,
	isSnippetSharingForSiteEnabled,
	onADFError,
	isAiSuggestionLoading = false,
	extraActions,
	formWarning,
}) => {
	const intl = useIntl();
	const [currentAdf, setCurrentAdf] = useState<DocNode>(initialAdf as DocNode);
	const [adfError, setADFError] = useState<unknown>(null);
	const { Modal } = useChoreographerAssets();

	const handleDocumentChange = (doc: DocNode) => {
		setCurrentAdf(doc);
	};

	const handleADFError = (error: Error | null) => {
		setADFError(error);
		onADFError?.(error);
	};

	const handleSubmit = async (values: SnippetBaseModalFormValues) => {
		await onSubmit(values, currentAdf);
	};

	useEffect(() => {
		// putting all the updates together doesn't run validation properly for some reason
		if (isAdfBodyEmpty(currentAdf)) {
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
			setADFError(EMPTY_ADF_ERROR);
		} else {
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
			setADFError(null);
		}
	}, [currentAdf]);

	const handleCancel = onCancel ?? onClose;

	return (
		<Modal width="95vw" shouldScrollInViewport onClose={onClose} onCloseComplete={onCloseComplete}>
			<Box xcss={styles.modalContent}>
				<SnippetBaseModalHeader title={title} onClose={onClose} />
				<Form<SnippetBaseModalFormValues> key={formKey} noValidate onSubmit={handleSubmit}>
					{({ submitting, formProps }) => (
						<form {...formProps} css={styles.formContainer}>
							<ModalBody>
								<Box xcss={styles.bodyWrapper}>
									<Box xcss={styles.bodyContainer}>
										<Box xcss={styles.previewColumn}>
											<SnippetEditor
												defaultValue={initialAdf}
												onDocumentChange={handleDocumentChange}
												onError={handleADFError}
												contentId={contentId}
												spaceId={spaceId}
												spaceKey={spaceKey}
												snippetId={snippetId}
												snippetScope={snippetScope}
											/>
										</Box>
										<Box xcss={styles.formColumn}>
											<Stack space="space.300" xcss={styles.formFields}>
												{formWarning}
												<SnippetCharacterCounterField<string, HTMLInputElement>
													name="snippetName"
													label={<FormattedMessage {...messages.nameLabel} />}
													defaultValue={defaultValues.snippetName}
													maxCharacters={MAX_SNIPPET_NAME_LENGTH}
													cutoff={SNIPPET_NAME_CUTOFF_LENGTH}
													validate={(value) =>
														value ? undefined : intl.formatMessage(messages.nameError)
													}
													isRequired
												>
													{({ fieldProps }) => (
														<AIInputField isLoading={isAiSuggestionLoading}>
															<Textfield
																{...fieldProps}
																placeholder={intl.formatMessage(messages.namePlaceholder)}
																// We change appearance to avoid a double border
																appearance={isAiSuggestionLoading ? 'subtle' : 'standard'}
																// We disable so that user changes are not overridden
																isDisabled={isAiSuggestionLoading}
															/>
														</AIInputField>
													)}
												</SnippetCharacterCounterField>
												<SnippetCharacterCounterField<string, HTMLTextAreaElement>
													name="snippetDescription"
													label={<FormattedMessage {...messages.descriptionLabel} />}
													defaultValue={defaultValues.snippetDescription}
													maxCharacters={MAX_SNIPPET_DESCRIPTION_LENGTH}
													cutoff={SNIPPET_DESCRIPTION_CUTOFF_LENGTH}
												>
													{({ fieldProps }) => (
														<AIInputField isLoading={isAiSuggestionLoading}>
															<TextArea
																{...fieldProps}
																minimumRows={5}
																placeholder={intl.formatMessage(messages.descriptionPlaceholder)}
																// We change appearance to avoid a double border
																appearance={isAiSuggestionLoading ? 'subtle' : 'standard'}
																// We disable so that user changes are not overridden
																isDisabled={isAiSuggestionLoading}
															/>
														</AIInputField>
													)}
												</SnippetCharacterCounterField>
												{isSnippetSharingForSiteEnabled && (
													<SnippetSharingField
														name="isShared"
														label={<FormattedMessage {...messages.saveToLabel} />}
														defaultValue={defaultValues.isShared}
													/>
												)}
												<Field
													name="snippetBody"
													defaultValue={adfError}
													validate={() => {
														if (adfError === EMPTY_ADF_ERROR) {
															return intl.formatMessage(messages.emptyBodyError);
														}
														if (adfError) {
															return intl.formatMessage(messages.adfError);
														}
														return undefined;
													}}
												>
													{({ error }) => (
														<MessageWrapper>
															{error && <ErrorMessage>{error}</ErrorMessage>}
														</MessageWrapper>
													)}
												</Field>
											</Stack>
											<Box xcss={styles.formActions}>
												<FormFooter align="end">
													{extraActions}
													<ButtonGroup>
														<Button appearance="subtle" onClick={handleCancel}>
															<FormattedMessage {...messages.cancelButton} />
														</Button>
														<Button type="submit" appearance="primary" isLoading={submitting}>
															{submitLabel}
														</Button>
													</ButtonGroup>
												</FormFooter>
											</Box>
										</Box>
									</Box>
								</Box>
							</ModalBody>
						</form>
					)}
				</Form>
			</Box>
		</Modal>
	);
};
